/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { lazy, Suspense, type ReactElement, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link } from '@/components/link';
import { User } from 'lucide-react';
import { useAuth } from '@/providers/auth';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { useDeferredUnmount } from '@/hooks/use-deferred-unmount';
import { OptionalLazyErrorBoundary } from '@/components/optional-lazy-error-boundary';

const UserMenu = lazy(() => import('./user-menu').then((module) => ({ default: module.UserMenu })));

export default function UserActions(): ReactElement {
    const session = useAuth();
    const { t } = useTranslation('header');
    const { t: tAccount } = useTranslation('account');
    const isAuthenticated: boolean = useMemo(() => {
        // Gate on userType only. Under a cached app shell the client restores userType from the
        // `__sfdc_usertype` hint cookie, but customerId is never carried in that hint — gating on it
        // too would wrongly keep a genuinely-registered visitor on the guest branch. userType is
        // authoritative: it is 'registered' iff the JWT carried a registered-customer id.
        return session?.userType === 'registered';
    }, [session]);

    const accountLink = isAuthenticated ? '/account/overview' : '/login';
    const ariaLabel = isAuthenticated ? tAccount('myAccount') : t('signIn');
    const icon = <User className="size-[18px]" strokeWidth={1.75} />;
    // RGBARRY-CUSTOMIZATION (perf): keep the hidden Radix popover and full account menu out of
    // the initial shell. The plain account link remains SSR-visible and usable; pointer intent
    // mounts the lazy overlay, and deferred unmount preserves its close animation.
    const [menuOpen, setMenuOpen] = useState(false);
    const menuMounted = useDeferredUnmount(menuOpen);
    const [menuContentMounted, setMenuContentMounted] = useState(false);
    const triggerRef = useRef<HTMLAnchorElement>(null);
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const menuContentId = useId();

    const openMenu = () => {
        if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
        setMenuOpen(true);
    };
    const scheduleMenuClose = () => {
        if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = setTimeout(() => setMenuOpen(false), 150);
    };
    useEffect(
        () => () => {
            if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
        },
        []
    );

    const trigger = (
        <Button
            variant="ghost"
            // Icon-only trigger. The Button size default sets `has-[>svg]:px-3` (12px) for icon
            // children, which sits in the same tailwind-merge group as the compact padding below,
            // so a bare `px-1` here is dead code. Use the `has-[>svg]:` modifier so the compact
            // mobile padding actually wins; without it the header row overflows a 320px viewport
            // and the mobile menu button is pushed off-screen (WCAG 1.4.10 Reflow).
            className="cursor-pointer lg:has-[>svg]:px-4 has-[>svg]:px-1 hover:bg-transparent hover:opacity-50 transition-opacity"
            asChild>
            <Link
                to={accountLink}
                aria-label={ariaLabel}
                aria-haspopup={menuOpen && menuContentMounted ? 'dialog' : undefined}
                aria-expanded={menuOpen && menuContentMounted ? true : undefined}
                aria-controls={menuOpen && menuContentMounted ? menuContentId : undefined}
                data-testid="user-account-trigger"
                ref={triggerRef}
                onMouseEnter={openMenu}
                onMouseLeave={scheduleMenuClose}>
                {icon}
            </Link>
        </Button>
    );

    return (
        <>
            {trigger}
            {menuMounted && (
                <OptionalLazyErrorBoundary>
                    <Suspense fallback={null}>
                        <UserMenu
                            isAuthenticated={isAuthenticated}
                            anchorRef={triggerRef}
                            contentId={menuContentId}
                            open={menuOpen}
                            onOpenChange={setMenuOpen}
                            onContentPresenceChange={setMenuContentMounted}
                            onHoverStart={openMenu}
                            onHoverEnd={scheduleMenuClose}
                        />
                    </Suspense>
                </OptionalLazyErrorBoundary>
            )}
        </>
    );
}
